'use client';

import {
  createContext,
  useContext,
  useReducer,
  FunctionComponent,
} from 'react';
import SwrLoader from '@/app/(zoc)/zoc/components/swrLoader';
import { useUser } from '@auth0/nextjs-auth0/client';
import { useUserProfile } from '@/app/context/userProfileContext';

export type SimpleAction<T> = {
  type: T;
};
export type Action<T, P> = SimpleAction<T> & {
  payload: P;
};

export const SET_SIDEBAR_CLOSED = 'SET_SIDEBAR_CLOSED';
export const SET_FILTERED_DATA = 'SET_FILTERED_DATA';
export const SET_IS_LOADING = 'SET_IS_LOADING';
export const SET_LOAD_ERROR = 'SET_LOAD_ERROR';
export const SET_SEARCH_STATE = 'SET_SEARCH_STATE';
export const SET_SEARCH_TERM = 'SET_SEARCH_TERM';
export const SET_ERROR_MESSAGE = 'SET_ERROR_MESSAGE';

export const SET_DATA = 'SET_DATA';

type SetSidebarClosed = Action<
  typeof SET_SIDEBAR_CLOSED,
  Pick<SidebarState, 'isSidebarClosed'>
>;
type SetFilteredData = Action<
  typeof SET_FILTERED_DATA,
  Pick<SidebarState, 'filteredData'>
>;
type SetIsLoading = Action<
  typeof SET_IS_LOADING,
  Pick<SidebarState, 'isLoading'>
>;
type SetLoadError = Action<
  typeof SET_LOAD_ERROR,
  Pick<SidebarState, 'loadError'>
>;
type SetSearchTerm = Action<
  typeof SET_SEARCH_TERM,
  Pick<SidebarState, 'searchTerm'>
>;
type SetSearchState = Action<
  typeof SET_SEARCH_STATE,
  Pick<SidebarState, 'searchState'>
>;
type SetErrorMessage = Action<
  typeof SET_ERROR_MESSAGE,
  Pick<SidebarState, 'errorMessage'>
>;
type SetData = Action<typeof SET_DATA, Pick<SidebarState, 'data'>>;

type SidebarAction =
  | SetSidebarClosed
  | SetFilteredData
  | SetIsLoading
  | SetLoadError
  | SetSearchState
  | SetSearchTerm
  | SetErrorMessage
  | SetData;

type Dispatch = (action: SidebarAction) => void;

type SearchState = 'open' | 'typing' | 'default';

type SidebarState = {
  isSidebarClosed: boolean;
  data: any;
  isLoading: boolean;
  loadError: boolean;
  errorMessage: string;
  searchState: SearchState;
  searchTerm: string;
  filteredData: any;
};

const initialState: SidebarState = {
  isSidebarClosed: false,
  data: [],
  filteredData: [],
  searchTerm: '',
  isLoading: true,
  loadError: false,
  errorMessage: '',
  searchState: 'default',
};

const SidebarContext = createContext<SidebarState>(initialState);
const SidebarDispatchContext = createContext<Dispatch | null>(null);

function sidebarReducer(state: SidebarState, action: SidebarAction) {
  switch (action.type) {
    case SET_SEARCH_STATE:
      return { ...state, searchState: action.payload.searchState };
    case SET_FILTERED_DATA:
      return { ...state, filteredData: action.payload.filteredData };
    case SET_SEARCH_TERM:
      const f = state.data?.filter((zoc: any) => {
        return (
          zoc?.class_name
            ?.toLowerCase()
            .includes(action.payload.searchTerm.toLowerCase()) ||
          zoc?.title
            ?.toLowerCase()
            .includes(action.payload.searchTerm.toLowerCase())
        );
      });
      return {
        ...state,
        ...{ searchTerm: action.payload.searchTerm, filteredData: f },
      };
    case SET_SIDEBAR_CLOSED:
      return { ...state, isSidebarClosed: action.payload.isSidebarClosed };
    case SET_LOAD_ERROR:
      return { ...state, loadError: action.payload.loadError };
    case SET_IS_LOADING: {
      return { ...state, isLoading: action.payload.isLoading };
    }
    case SET_ERROR_MESSAGE: {
      return { ...state, errorMessage: action.payload.errorMessage };
    }
    case SET_DATA: {
      return { ...state, data: action.payload.data };
    }
    default: {
      throw new Error(`Unsupported action type: ${action}`);
    }
  }
}

function SidebarProvider({ children }: any) {
  const [state, dispatch] = useReducer(sidebarReducer, initialState);
  const { user } = useUser();
  const { userProfile } = useUserProfile();

  return (
    <SidebarContext.Provider value={state}>
      <SidebarDispatchContext.Provider value={dispatch}>
        {/*only fetch first zoc list when we have user profile and know onboarding informed is true*/}
        {user && userProfile?.user_flags.onboarding_informed && <SwrLoader />}
        {children}
      </SidebarDispatchContext.Provider>
    </SidebarContext.Provider>
  );
}

function SidebarConsumer({ children }: any) {
  return (
    <SidebarContext.Consumer>
      {(context) => {
        if (context === undefined) {
          throw new Error(
            'SidebarConsumer must be used within a SidebarProvider'
          );
        }
        // @ts-ignore
        return children(context);
      }}
    </SidebarContext.Consumer>
  );
}

function useSidebarState() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error(`useSidebarState must be used within a SidebarProvider`);
  }
  return context;
}

function useSidebarDispatch() {
  const context = useContext(SidebarDispatchContext);
  if (!context) {
    throw new Error(`useSidebarDispatch must be used within a SidebarProvider`);
  }
  return context;
}

function useSidebar(): [SidebarState, Dispatch] {
  return [useSidebarState(), useSidebarDispatch()];
}

function withSidebar<P>(
  Component: FunctionComponent<P & SidebarState & { dispatch: Dispatch }>
) {
  return function WrappedComponent(props: P): React.ReactElement {
    const [state, dispatch] = useSidebar();
    return <Component {...props} {...state} dispatch={dispatch} />;
  };
}

export {
  SidebarProvider,
  SidebarContext,
  withSidebar,
  useSidebarState,
  useSidebarDispatch,
  useSidebar,
};
